import { useQuery } from '@tanstack/react-query'
import React, { useMemo, useState } from 'react'
import { useBillingContext } from '~/contexts/BillingContext'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { useUsageDashboard } from '~/hooks/usage'
import { getSuperAdminCreditBalance } from '~/services/superAdminService'
import type { BillableFlowDetailsParams } from '~/services/usageDashboardService'
import { cm } from '~/utils/utils'
import { Typography } from '../common/Typography'
import { ActiveDevelopersCard } from './ActiveDevelopersCard'
import { ApiKeyUsageTable } from './ApiKeyUsageTable'
import { type TimeRangeMode, UsageDisplayContent } from './DialogUsageDisplayContent'
import { downloadDashboardAsExcel } from './downloadReport'
import { CloseIcon, DashboardIcon, DownloadIcon } from './icons'
import { LeaderboardTable } from './LeaderboardTable'
import './styles.css'
import { getIsBillableFlowPlan } from './types'
import { UsageBarChart, utcCalendarDateFromBackendIso } from './UsageBarChart'
import { UsageSidebar } from './UsageSidebar'
import {
  resolveUsageApiRange,
  useCustomDateRangeFromLicense
} from './useCustomDateRangeFromLicense'

const BillableFlowsPanel = React.lazy(() =>
  import('./BillableFlowsTable').then(mod => ({ default: mod.BillableFlowsPanel }))
)

interface DashboardContentProps {
  /** Show sidebar navigation (Account, People, Billing, etc.) */
  showSidebar?: boolean
  /** Show header with title and close button */
  showHeader?: boolean
  /** Callback when close button is clicked (only relevant if showHeader=true) */
  onClose?: () => void
  /** Active navigation item for sidebar (only relevant if showSidebar=true) */
  activeNavItem?: string
  /** Callback when sidebar navigation changes (only relevant if showSidebar=true) */
  onNavigate?: (item: string) => void
  /** Use fixed dimensions (for modal/standalone view) vs flexible (for embedded view) */
  fixedLayout?: boolean
  className?: string
  /**
   * When set (e.g. super admin account management), load dashboard and period data for this
   * billing account instead of the signed-in user’s BillingContext account.
   */
  billingAccountIdOverride?: number
  /** Export / metadata email when using billingAccountIdOverride */
  billingEmailOverride?: string | null
  /** Disable exports and flow-detail drill-ins (e.g. super admin view-only). */
  readOnly?: boolean
  /** When true, surfaces super-admin-only data such as credit-usage cards on
   *  billable-flow plans. The parent (`ManageAccountDialog`) computes this as
   *  `isSuperAdmin || isSuperAdminImpersonation`, so both a super admin viewing
   *  their own org and the cross-org super-admin tool path qualify. */
  isSuperAdminView?: boolean
}

export const DashboardContent: React.FC<DashboardContentProps> = ({
  showSidebar = false,
  showHeader = false,
  onClose,
  activeNavItem: externalActiveNavItem,
  onNavigate: externalOnNavigate,
  fixedLayout = false,
  className,
  billingAccountIdOverride,
  billingEmailOverride,
  readOnly = false,
  isSuperAdminView = false
}) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [isDownloading, setIsDownloading] = useState(false)
  const [selectedWorkspaceUuid, setSelectedWorkspaceUuid] = useState<string | undefined>(undefined)
  const [timeRangeMode, setTimeRangeMode] = useState<TimeRangeMode>('billing_period')
  const [internalActiveNavItem, setInternalActiveNavItem] = useState('dashboard')

  const [showFlowsPanel, setShowFlowsPanel] = useState(false)

  const { currentWorkspace, workspaces } = useWorkspaceContext()
  const { billingInfo, allLicenses } = useBillingContext()

  const { data: superAdminCreditBalance } = useQuery({
    queryKey: ['superAdminCreditBalance', billingAccountIdOverride],
    queryFn: () => getSuperAdminCreditBalance(billingAccountIdOverride!),
    enabled: billingAccountIdOverride != null && billingAccountIdOverride > 0
  })

  const periodStartForRange = billingAccountIdOverride
    ? superAdminCreditBalance?.period_start
    : allLicenses?.period_start
  const periodEndForRange = billingAccountIdOverride
    ? superAdminCreditBalance?.period_end
    : allLicenses?.period_end

  const { customStartDate, customEndDate, onCustomStartDateChange, onCustomEndDateChange } =
    useCustomDateRangeFromLicense(
      billingAccountIdOverride ?? billingInfo?.id,
      periodStartForRange,
      periodEndForRange
    )

  // Use external nav state if provided, otherwise internal
  const activeNavItem = externalActiveNavItem ?? internalActiveNavItem
  const handleNavigate = externalOnNavigate ?? setInternalActiveNavItem

  // Determine API time_range and dates based on mode.
  // resolveUsageApiRange caps any future license_end at today so the chart doesn't
  // render empty future buckets (e.g. yearly Enterprise, sentinel 2099-12-31). The
  // trigger label is independent and still shows the contractual end date.
  const {
    timeRange: apiTimeRange,
    start: startDateParam,
    end: endDateParam
  } = useMemo(
    () =>
      resolveUsageApiRange({
        mode: timeRangeMode,
        customStart: customStartDate,
        customEnd: customEndDate,
        licenseStart: periodStartForRange,
        licenseEnd: periodEndForRange
      }),
    [timeRangeMode, customStartDate, customEndDate, periodStartForRange, periodEndForRange]
  )

  const { data, isLoading, error } = useUsageDashboard(
    apiTimeRange,
    currentPage,
    10,
    selectedWorkspaceUuid,
    startDateParam,
    endDateParam,
    billingAccountIdOverride
  )

  const isBillableFlowPlan = getIsBillableFlowPlan(
    data?.credits,
    billingAccountIdOverride ? null : allLicenses
  )

  const effectiveBillingId = billingAccountIdOverride ?? billingInfo?.id

  /**
   * Fallback anchor before/with API data. Backend daily buckets are UTC calendar days
   * (see usage_dashboard._build_chart_points_from_daily_maps); stepping must use `utc`
   * so expandWeeklyChartForDisplay indices match `weekly_chart` rows — including for
   * custom (date_range) mode after timezone-aware range conversion.
   */
  const usageChartSeriesAnchor = useMemo(() => {
    if (timeRangeMode === 'date_range') {
      const anchor = customStartDate
        ? utcCalendarDateFromBackendIso(`${customStartDate}T00:00:00Z`)
        : null
      if (!anchor) return null
      const endDate = customEndDate
        ? (utcCalendarDateFromBackendIso(`${customEndDate}T00:00:00Z`) ?? undefined)
        : undefined
      return { anchorDate: anchor, calendar: 'utc' as const, displayEndDate: endDate }
    }
    const ps = billingAccountIdOverride
      ? superAdminCreditBalance?.period_start
      : allLicenses?.period_start
    const anchor = ps ? utcCalendarDateFromBackendIso(ps) : null
    if (!anchor) return null
    return { anchorDate: anchor, calendar: 'utc' as const }
  }, [
    billingAccountIdOverride,
    superAdminCreditBalance?.period_start,
    allLicenses?.period_start,
    customStartDate,
    customEndDate,
    timeRangeMode
  ])

  /** Prefer API `weekly_chart_starts_on` (UTC YYYY-MM-DD) so expand matches the real series window. */
  const barChartSeriesAnchor = useMemo(() => {
    const raw = data?.weekly_chart_starts_on
    if (raw && data?.weekly_chart?.length && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const anchor = utcCalendarDateFromBackendIso(`${raw}T00:00:00Z`)
      if (anchor) {
        return {
          anchorDate: anchor,
          calendar: 'utc' as const,
          displayEndDate: usageChartSeriesAnchor?.displayEndDate
        }
      }
    }
    return usageChartSeriesAnchor
  }, [data?.weekly_chart_starts_on, data?.weekly_chart?.length, usageChartSeriesAnchor])

  const flowDetailsParams: BillableFlowDetailsParams = useMemo(
    () => ({
      timeRange: apiTimeRange,
      workspaceUuid: selectedWorkspaceUuid,
      startDate: startDateParam,
      endDate: endDateParam,
      personalOnly: false
    }),
    [apiTimeRange, selectedWorkspaceUuid, startDateParam, endDateParam]
  )

  const exportWorkspaceName = selectedWorkspaceUuid
    ? workspaces?.find(w => w.uuid === selectedWorkspaceUuid)?.name
    : undefined

  const handleDownloadExcel = () => {
    if (readOnly || !data || isDownloading) return
    setIsDownloading(true)
    try {
      downloadDashboardAsExcel(data, 'usage-dashboard-report', {
        workspaceName: exportWorkspaceName ?? currentWorkspace?.name,
        billingEmail: billingEmailOverride ?? billingInfo?.billing_email,
        reportName: 'Usage Report'
      })
    } finally {
      setIsDownloading(false)
    }
  }

  const downloadButton = (
    <button
      onClick={handleDownloadExcel}
      className='h-[36px] flex items-center gap-[8px] px-[16px] py-[8px] rounded-[12px] cursor-pointer hover:bg-[#F5F5F5] transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
      disabled={readOnly || isDownloading || !data}
    >
      <DownloadIcon className='w-[16px] h-[16px] text-[#8E8E8E]' />
      <Typography
        fontFamily='geist'
        fontSize='bodyMd'
        fontWeight='medium'
        className='select-none'
        style={{ color: 'rgba(0, 0, 0, 0.65)', lineHeight: '22px', fontSize: '14px' }}
      >
        {isDownloading ? 'Downloading...' : 'Download full report'}
      </Typography>
    </button>
  )

  // Main content (shared between both layouts)
  const mainContent = (
    <>
      {/* Loading state */}
      {isLoading && (
        <div className='mt-4 text-sm text-gray-500 select-none'>Loading dashboard data...</div>
      )}

      {/* Error state */}
      {error && (
        <div className='mt-4 text-sm text-red-500 select-none'>Failed to load dashboard data.</div>
      )}

      {/* Dashboard content */}
      <div className='flex flex-col'>
        <UsageDisplayContent
          usageOverview={data?.usage_overview}
          credits={data?.credits}
          selectedWorkspaceUuid={selectedWorkspaceUuid}
          onWorkspaceChange={setSelectedWorkspaceUuid}
          timeRangeMode={timeRangeMode}
          onTimeRangeModeChange={setTimeRangeMode}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          onCustomStartDateChange={onCustomStartDateChange}
          onCustomEndDateChange={onCustomEndDateChange}
          billingPeriodForDisplay={
            billingAccountIdOverride
              ? {
                  periodStart: superAdminCreditBalance?.period_start,
                  periodEnd: superAdminCreditBalance?.period_end
                }
              : undefined
          }
          showWorkspaceFilter={true}
          showTimeRangeSelector={true}
          layout='horizontal'
          headerActions={readOnly ? null : downloadButton}
          onCreditsDetailsClick={readOnly ? undefined : () => setShowFlowsPanel(true)}
          isSuperAdmin={isSuperAdminView}
          creditsSectionTitle='Team Credit Usage'
          billableFlowsTitle='Team Billable Flows'
        >
          {data && (
            <>
              {/* Bar Chart */}
              <div className='mb-6'>
                <UsageBarChart
                  data={data.weekly_chart}
                  seriesAnchor={barChartSeriesAnchor}
                  chartBucket={data.chart_bucket ?? 'day'}
                  className='w-full'
                />
              </div>

              {/* Active Developers */}
              <div className='mb-4'>
                <ActiveDevelopersCard count={data.active_developers} className='w-full h-auto' />
              </div>

              {/* Billable Flows Panel (modal, opened via CreditsCard click) */}
              {effectiveBillingId != null && showFlowsPanel && !readOnly && (
                <React.Suspense fallback={null}>
                  <BillableFlowsPanel
                    isOpen={showFlowsPanel}
                    onClose={() => setShowFlowsPanel(false)}
                    billingAccountId={effectiveBillingId}
                    params={flowDetailsParams}
                    hideCredits={isBillableFlowPlan}
                    isSuperAdmin={isSuperAdminView}
                  />
                </React.Suspense>
              )}

              {/* Leaderboard */}
              <div className='flex-1'>
                <LeaderboardTable
                  data={data.leaderboard}
                  pagination={{ ...data.pagination, current_page: currentPage }}
                  onPageChange={setCurrentPage}
                  className='w-full h-auto'
                  isBillableFlowPlan={isBillableFlowPlan}
                />
              </div>

              {/* API Keys — hidden when no API token had activity in the window */}
              {data.api_key_usage && data.api_key_usage.length > 0 && (
                <div className='mt-4'>
                  <ApiKeyUsageTable
                    data={data.api_key_usage}
                    className='w-full h-auto'
                    showCredits={!isBillableFlowPlan || isSuperAdminView}
                  />
                </div>
              )}
            </>
          )}
        </UsageDisplayContent>
      </div>
    </>
  )

  // Fixed layout with sidebar and header (standalone/modal view)
  if (fixedLayout || showSidebar || showHeader) {
    return (
      <div
        className={cm(
          'flex bg-white rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.03),0_1px_6px_-1px_rgba(0,0,0,0.02),0_2px_4px_rgba(0,0,0,0.02)]',
          fixedLayout && 'w-[1058px] h-[1617px]',
          className
        )}
      >
        {/* Sidebar */}
        {showSidebar && <UsageSidebar activeItem={activeNavItem} onNavigate={handleNavigate} />}

        {/* Main Content Area */}
        <div
          className={cm(
            'flex flex-col bg-white overflow-auto',
            showSidebar ? 'w-[878px]' : 'flex-1',
            fixedLayout && 'h-full'
          )}
        >
          {/* Header */}
          {showHeader && (
            <div className='w-full h-20 flex items-center px-12 border-b border-[#EAEAEA] flex-shrink-0'>
              <DashboardIcon className='w-6 h-6 mr-3' />
              <Typography
                fontFamily='geist'
                fontSize='titleMd'
                fontWeight='bold'
                className='select-none'
                style={{ color: '#000000', lineHeight: '24px', flex: 1 }}
              >
                Dashboard
              </Typography>
              {onClose && (
                <button
                  onClick={onClose}
                  className='w-8 h-8 flex items-center justify-center cursor-pointer rounded-[8px] hover:bg-[rgba(0,0,0,0.04)] transition-colors'
                >
                  <CloseIcon className='w-6 h-6' />
                </button>
              )}
            </div>
          )}

          {/* Content Body */}
          <div className='flex-1 pt-6 px-12 pb-6 overflow-auto'>{mainContent}</div>
        </div>
      </div>
    )
  }

  // Flexible layout (embedded view - default)
  return <div className={cm('flex flex-col', className)}>{mainContent}</div>
}

export default DashboardContent
